/**
 * Specialized Skeleton Components for different content types
 * 
 * Pre-built skeleton layouts matching actual components
 */

import { Skeleton } from './Skeleton';

// EventCard Skeleton - matches EventCard layout
export function EventCardSkeleton() {
  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl overflow-hidden shadow-card min-w-[280px]">
      <div className="animate-pulse">
        {/* Image placeholder */}
        <Skeleton variant="image" className="h-40 w-full rounded-none" />
        
        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Title */}
          <Skeleton className="h-5 w-4/5" />
          
          {/* Date/Time */}
          <Skeleton className="h-3 w-2/3" />
          
          {/* Location and attendees */}
          <div className="flex gap-2">
            <Skeleton className="h-3 w-1/4" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ActivityCard Skeleton - matches ActivityCard layout
export function ActivityCardSkeleton() {
  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
      <div className="animate-pulse">
        <div className="flex items-center gap-3 mb-3">
          {/* Avatar */}
          <Skeleton variant="circular" className="w-12 h-12 flex-shrink-0" />
          
          {/* Name and club info */}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
        
        {/* Activity description */}
        <Skeleton className="h-3 w-full mb-2" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    </div>
  );
}

// NextMoveCard Skeleton - matches NextMoveCard layout
export function NextMoveCardSkeleton() {
  return (
    <div className="bg-gradient-to-br from-oro-surface to-oro-surface-alt border border-oro-border rounded-xl p-6 shadow-glow">
      <div className="animate-pulse space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="w-6 h-6 rounded" />
        </div>
        
        {/* Title */}
        <div className="space-y-2">
          <Skeleton className="h-7 w-3/4" />
          <Skeleton className="h-7 w-1/2" />
        </div>
        
        {/* Details */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Skeleton className="w-4 h-4 rounded" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="w-4 h-4 rounded" />
            <Skeleton className="h-4 w-16" />
          </div>
        </div>
        
        {/* Action button */}
        <Skeleton className="h-12 w-full rounded-lg" />
      </div>
    </div>
  );
}

// ProfileHeader Skeleton - for profile pages
export function ProfileHeaderSkeleton() {
  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-6 shadow-card text-center">
      <div className="animate-pulse space-y-4">
        {/* Avatar */}
        <Skeleton variant="circular" className="w-24 h-24 mx-auto" />
        
        {/* Name */}
        <Skeleton className="h-5 w-1/2 mx-auto" />
        
        {/* Location */}
        <Skeleton className="h-3 w-1/3 mx-auto" />
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-oro-border">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-3 w-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ChatMessage Skeletons - for chat loading
export function ChatMessageSkeleton({ isOutgoing = false }: { isOutgoing?: boolean }) {
  return (
    <div className={`flex gap-2 items-start ${isOutgoing ? 'justify-end' : ''}`}>
      {!isOutgoing && (
        <Skeleton variant="circular" className="w-8 h-8 flex-shrink-0" />
      )}
      
      <div className={`animate-pulse rounded-2xl p-3 max-w-[70%] ${
        isOutgoing 
          ? 'bg-accent/20 rounded-tr-sm' 
          : 'bg-oro-surface-alt rounded-tl-sm'
      }`}>
        <Skeleton className={`h-3 w-32 mb-2 ${isOutgoing ? 'bg-accent/40' : 'bg-oro-bg'}`} />
        <Skeleton className={`h-3 w-24 ${isOutgoing ? 'bg-accent/40' : 'bg-oro-bg'}`} />
      </div>
    </div>
  );
}

// List Item Skeleton - for member lists, etc.
export function ListItemSkeleton() {
  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
      <div className="animate-pulse flex items-center gap-3">
        {/* Avatar */}
        <Skeleton variant="circular" className="w-12 h-12 flex-shrink-0" />
        
        {/* Content */}
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    </div>
  );
}

// Button Skeleton
export function ButtonSkeleton({ className }: { className?: string }) {
  return <Skeleton className={`h-12 rounded-lg ${className}`} />;
}

// Grid Skeleton - for club/member grids
export function GridSkeleton({ 
  items = 6, 
  columns = 3 
}: { 
  items?: number; 
  columns?: number; 
}) {
  return (
    <div className={`grid grid-cols-${columns} gap-3`}>
      {[...Array(items)].map((_, i) => (
        <Skeleton key={i} className="aspect-square rounded-lg" />
      ))}
    </div>
  );
}

// ClubCard Skeleton - for clubs grid/list
export function ClubCardSkeleton() {
  return (
    <div className="bg-oro-surface border border-oro-border rounded-2xl overflow-hidden shadow-card">
      <div className="animate-pulse">
        {/* Hero image */}
        <Skeleton variant="image" className="h-32 w-full rounded-none" />
        
        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Club name */}
          <Skeleton className="h-4 w-3/4" />
          
          {/* Description */}
          <div className="space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
          
          {/* Stats */}
          <div className="space-y-2 pt-1">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-28" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Dashboard Skeleton - for admin/management pages from shared/flows
export function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="animate-pulse">
        {/* Header with stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-oro-surface border border-oro-border rounded-lg p-4">
              <Skeleton className="h-6 w-16 mb-2" />
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </div>
        
        {/* Main content sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent activity */}
          <div className="bg-oro-surface border border-oro-border rounded-lg p-4">
            <Skeleton className="h-5 w-32 mb-4" />
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton variant="circular" className="w-8 h-8" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-3 w-3/4" />
                    <Skeleton className="h-2 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Charts/analytics */}
          <div className="bg-oro-surface border border-oro-border rounded-lg p-4">
            <Skeleton className="h-5 w-28 mb-4" />
            <Skeleton className="h-48 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}